import { getFragmentContent } from "../../Pages/Fragments/fragmentsClient";

/**
 * Block-fragment preview overlay. The home dashboard widgets — the MarketsBoard cards (cours de change /
 * inflation / taux d'intérêt), the FxRatesExplorer converter, the InformationHub and the governors roster —
 * are block-fragments: at deploy time their page slot is reduced to `{ fragmentKey }` and the real data
 * lives in one CDN file `fragments/{lang}/{key}.json` (see ContentService.ConvertBlockFragmentRefs /
 * ApaBlockFragments). The public site fills the widget from that file at request time
 * (resolveDynamicComponents).
 *
 * For the admin page-editor preview to MATCH what will deploy — instead of re-projecting one widget
 * (inflation) client-side from the DB while the others render blank — we fetch those SAME baked fragment
 * files and overlay them onto the previewed components here. So every computed widget shows its live
 * fragment data, uniformly, and updates whenever the fragment re-bakes (a data change).
 */

/** Stable block-fragment keys — mirror ApaBlockFragmentKeys (C#). */
export const BLOCK_FRAGMENT_KEYS = {
	marketsExchange: "markets-exchange",
	marketsInflation: "markets-inflation",
	marketsInterest: "markets-interest",
	keyIndicatorsBar: "key-indicators-bar",
	informationHub: "information-hub",
	fxRatesExplorer: "fx-rates-explorer",
	governors: "governors"
} as const;

export const ALL_BLOCK_FRAGMENT_KEYS: ReadonlyArray<string> = Object.values(BLOCK_FRAGMENT_KEYS);

export type BlockFragmentMap = Record<string, Record<string, unknown>>;

/** MarketsBoard sub-block field → fragment key (the deploy-time slot mapping). */
const MARKETS_SUBBLOCK_KEYS: Record<string, string> = {
	exchange: BLOCK_FRAGMENT_KEYS.marketsExchange,
	inflation: BLOCK_FRAGMENT_KEYS.marketsInflation,
	interest: BLOCK_FRAGMENT_KEYS.marketsInterest
};

/** Standalone component type → fragment key (the component's whole content is the fragment). */
const COMPONENT_KEYS: Record<string, string> = {
	FxRatesExplorer: BLOCK_FRAGMENT_KEYS.fxRatesExplorer,
	InformationHub: BLOCK_FRAGMENT_KEYS.informationHub,
	GovernorList: BLOCK_FRAGMENT_KEYS.governors
};

/**
 * Fetch every block-fragment's baked content for an environment, in parallel. Fragments that are not
 * baked (or fail to load) are simply absent from the map — the overlay then leaves that slot untouched,
 * so the SDK renderer shows its "déployé" placeholder exactly as the live site would on a miss.
 */
export async function loadBlockFragments(
	environment: "preview" | "production",
	language = "fr"
): Promise<BlockFragmentMap> {
	const entries = await Promise.all(
		ALL_BLOCK_FRAGMENT_KEYS.map(async (key) => [key, await getFragmentContent(key, environment, language)] as const)
	);
	const map: BlockFragmentMap = {};
	for (const [key, value] of entries) {
		if (value && typeof value === "object") map[key] = value as Record<string, unknown>;
	}
	return map;
}

/**
 * Overlay the baked block-fragments onto one previewed component. Returns a component whose computed
 * widgets carry the SAME data the live site renders; a component with no block-fragment slot (or whose
 * fragment isn't baked) is returned unchanged.
 */
export function overlayBlockFragments<T extends { componentType?: string; content?: unknown }>(
	component: T,
	fragments: BlockFragmentMap
): T {
	const type = component.componentType;
	if (!type) return component;

	if (type === "MarketsBoard") {
		const content = { ...((component.content as Record<string, unknown>) ?? {}) };
		let changed = false;
		for (const [field, key] of Object.entries(MARKETS_SUBBLOCK_KEYS)) {
			if (content[field] != null && fragments[key]) {
				content[field] = fragments[key];
				changed = true;
			}
		}
		return changed ? { ...component, content } : component;
	}

	const componentKey = COMPONENT_KEYS[type];
	if (componentKey && fragments[componentKey]) {
		return { ...component, content: fragments[componentKey] };
	}
	return component;
}
